<template>
    <MainComponent v-if="isDismissFilterReady && (hasSuggestions || taskListCompleted)" data-testid="suggested-for-you-section">
        <template #title>
            <h2>{{ $t('profile.section.suggested.for.you.title') }}</h2>
        </template>
        <template #title-action>
            <VJoyTag size="small" variant="inactive">
                <VJoyIcon name="eye" size="xsmall" />
                <span class="suggested-for-you__visibility-label">{{ $t('profile.section.only.visible.to.you') }}</span>
            </VJoyTag>
            <template v-if="hasSuggestions">
                <VJoyIconButton
                    :class="{'suggested-for-you__arrow--disabled': isAtStart}"
                    icon="chevron-left"
                    size="small"
                    variant="ghost"
                    @click="scrollCards(-1)"
                />
                <VJoyIconButton
                    :class="{'suggested-for-you__arrow--disabled': isAtEnd}"
                    icon="chevron-right"
                    size="small"
                    variant="ghost"
                    @click="scrollCards(1)"
                />
            </template>
        </template>
        <template #content>
            <VJoyWrapper
                v-if="hasSuggestions"
                ref="carouselRef"
                align="stretch"
                class="suggested-for-you__carousel"
                column-gap="4"
                direction="row"
                no-margin
                wrap="nowrap"
            >
                <SuggestionCard
                    v-for="suggestion in suggestions"
                    :key="suggestion.type"
                    :malt-project-id="suggestion.maltProjectId"
                    :type="suggestion.type"
                    @dismiss="dismissSuggestion(suggestion.type)"
                />
            </VJoyWrapper>
            <VJoyHighlight v-else display-icon level="success">
                <template #highlight-title>{{ $t('profile.section.suggested.for.you.empty.title') }}</template>
                {{ $t('profile.section.suggested.for.you.empty.subtitle') }}
            </VJoyHighlight>
        </template>
    </MainComponent>
</template>

<script lang="ts" setup>
    import {type ComponentPublicInstance, onMounted, ref, useTemplateRef, watchEffect} from 'vue';
    import {storeToRefs} from 'pinia';
    import {useDataTracking} from '#imports';
    import {VJoyIconButton, VJoyHighlight, VJoyIcon, VJoyTag, VJoyWrapper} from '@maltjoy/core-vue';
    import type {ProfileSuggestionType} from '@api';
    import MainComponent from '~/components/main/common/MainComponent.vue';
    import {TrackingEvents} from '~/config/TrackingEvents';
    import SuggestionCard from './SuggestionCard.vue';
    import {useProfileSuggestionsStore} from '../store/profile-suggestions.store';

    const store = useProfileSuggestionsStore();
    const {suggestions, hasSuggestions, taskListCompleted} = storeToRefs(store);
    const {dismissSuggestion, applyDismissFilter} = store;

    const isDismissFilterReady = ref(false);
    const {track} = useDataTracking();

    const carouselRef = useTemplateRef<ComponentPublicInstance>('carouselRef');
    const CARD_MIN_WIDTH = 280;
    const CARD_GAP = 16;
    const SCROLL_AMOUNT = CARD_MIN_WIDTH + CARD_GAP;

    const isAtStart = ref(true);
    const isAtEnd = ref(false);

    function updateArrows() {
        const el = carouselRef.value?.$el;
        if (!el) return;
        isAtStart.value = el.scrollLeft <= 0;
        isAtEnd.value = el.scrollLeft + el.clientWidth >= el.scrollWidth;
    }

    function scrollCards(delta: number) {
        carouselRef.value?.$el?.scrollBy({left: delta * SCROLL_AMOUNT, behavior: 'smooth'});
    }

    watchEffect((onCleanup) => {
        const el = carouselRef.value?.$el;
        if (!el) return;
        el.addEventListener('scroll', updateArrows);
        updateArrows();
        onCleanup(() => el.removeEventListener('scroll', updateArrows));
    });

    const SUGGESTION_EDITION_SECTION: Record<ProfileSuggestionType, string> = {
        ONGOING_EXPERIENCE: 'experiences',
        RECENT_EXPERIENCE: 'experiences',
        PORTFOLIO: 'portfolio',
        MALT_PROJECT: 'experiences',
        RECOMMENDATION: 'recommendations',
    };

    onMounted(() => {
        applyDismissFilter();
        isDismissFilterReady.value = true;

        if (suggestions.value.length > 0) {
            track(TrackingEvents.SUGGESTED_FOR_YOU_VIEWED, {
                number_of_cards: suggestions.value.length,
                cards_displayed: suggestions.value.map((s) => s.type.toLowerCase()),
                page_type: 'profile',
            });

            suggestions.value.forEach((suggestion) => {
                track(TrackingEvents.SUGGESTED_FOR_YOU_CARD_VIEWED, {
                    card_type: suggestion.type.toLowerCase(),
                    page_type: 'profile',
                });
                track(TrackingEvents.PROFILE_EDITION_NUDGE_VIEWED, {
                    nudge_type: 'suggested_for_you',
                    card_name: suggestion.type.toLowerCase(),
                    is_card_checked: false,
                    profile_edition_section: SUGGESTION_EDITION_SECTION[suggestion.type],
                    page_type: 'profile',
                });
            });
        }
    });
</script>

<style lang="scss" scoped>
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;

    :deep(.joy-highlight) {
        align-items: center;
    }

    .suggested-for-you {
        &__visibility-label {
            @include mq.screen_xxs {
                display: none;
            }
        }

        &__arrow--disabled {
            opacity: 0.4;
            pointer-events: none;
        }

        &__carousel {
            overflow-x: auto;
            -webkit-overflow-scrolling: touch;
            overscroll-behavior-x: contain;
            scrollbar-width: none;
            -ms-overflow-style: none;

            &::-webkit-scrollbar {
                display: none;
            }
        }
    }
</style>
